import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import config from '../config/auth0';
import { useApiToken } from '../hooks/useApiToken';

const AcceptInvitePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const getAccessToken = useApiToken();
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      setInviteToken(token);
    } else {
      setError('No invitation token found.');
    }
  }, [location.search]);

  useEffect(() => {
    if (!isLoading && inviteToken) {
      if (!isAuthenticated) {
        // If not authenticated, redirect to Auth0 login, then back to this page
        loginWithRedirect({
          appState: { targetUrl: location.pathname + location.search },
        });
      } else if (user) {
        // Authenticated and have token, now call backend API
        const acceptInvitation = async () => {
          try {
            const accessToken = await getAccessToken();
            const response = await fetch(`${config.fastApiBaseUrl}/api/onboard/accept-invitation`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                token: inviteToken,
                auth0_user_id: user.sub,
                email: user.email,
              }),
            });
            
            if (response.ok) {
              console.log('Invitation accepted successfully!');
              navigate('/dashboard');
            } else {
              const errorData = await response.json();
              console.error('Failed to accept invitation:', errorData);
              setError(errorData.message || 'Failed to accept invitation');
            }
          } catch (error) {
            console.error('Error during invitation acceptance:', error);
            setError('Network error occurred while processing invitation.');
          }
        };
        acceptInvitation();
      }
    }
  }, [isLoading, isAuthenticated, user, inviteToken, loginWithRedirect, navigate, getAccessToken, location.pathname, location.search]);

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            py: 4,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 3,
              width: '100%',
            }}
          >
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              Please contact your administrator for assistance.
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 3,
            width: '100%',
            textAlign: 'center',
          }}
        >
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Processing Invitation
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {inviteToken ? 'Setting up your account...' : 'Loading invitation...'}
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default AcceptInvitePage; 